// Choosing a group's toolchains (D90): the presets as checkable cards, each marked when the
// workflow requires it, and the root setup script for those who may write one (or to clear one
// already there). Controlled: the card and the wizard hold the values.
import { Check } from "@phosphor-icons/react";
import { Textarea } from "rizzui";

import { TOOLCHAINS, type Toolchain } from "../../catalog/types";
import { useT } from "../../i18n";
import { cn } from "../../ui/cn";
import { Pill } from "../../ui/status";

interface Props {
  presets: Toolchain[];
  onPresets: (next: Toolchain[]) => void;
  /** Required by the workflow: shown as such, and the card says so if unticked. */
  required?: Toolchain[];
  script?: string;
  onScript?: (next: string) => void;
  /** Whether the script box shows: the user may write one, or one is set. */
  scriptShown?: boolean;
  scriptLocked?: boolean;
}

export function ToolchainsEditor({ presets, onPresets, required = [], script = "", onScript, scriptShown, scriptLocked }: Props) {
  const { tasks, workflows } = useT();
  const t = tasks.toolchains;
  const names = workflows.needs.requirements.toolchainNames;
  const toggle = (tc: Toolchain) => onPresets(presets.includes(tc) ? presets.filter((p) => p !== tc) : [...presets, tc]);
  return (
    <div className="space-y-4">
      <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(11rem,1fr))]" role="group" aria-label={t.presets}>
        {TOOLCHAINS.map((tc) => {
          const on = presets.includes(tc);
          return (
            <button
              key={tc}
              type="button"
              aria-pressed={on}
              onClick={() => toggle(tc)}
              className={cn(
                "relative flex flex-col items-start gap-1 rounded-xl border p-3 text-start transition-colors",
                on ? "border-primary bg-primary-lighter/40" : "border-muted hover:border-gray-300",
              )}
            >
              <span className="text-[13.5px] font-semibold text-gray-900">{names[tc]}</span>
              <span className="text-xs leading-snug text-gray-500">{t.hints[tc]}</span>
              {required.includes(tc) ? (
                <Pill tone={on ? "primary" : "orange"} className="mt-1">
                  {t.requiredByWorkflow}
                </Pill>
              ) : null}
              {on ? <Check weight="bold" className="absolute end-3 top-3 h-4 w-4 text-primary" aria-hidden /> : null}
            </button>
          );
        })}
      </div>
      {scriptShown && onScript ? (
        <div>
          <Textarea
            label={t.script}
            value={script}
            rows={5}
            disabled={scriptLocked}
            placeholder={t.scriptPlaceholder}
            textareaClassName="font-mono text-[12.5px]"
            onChange={(e) => onScript(e.target.value)}
          />
          <p className="mb-0 mt-1.5 text-[13px] text-gray-500">{scriptLocked ? t.scriptLocked : t.scriptHint}</p>
        </div>
      ) : null}
    </div>
  );
}
